import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Empty, Image, Input, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { PictureOutlined, ReloadOutlined } from '@ant-design/icons';
import { get, post } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './AIManagement.css';

const { TextArea } = Input;

interface PlatformConfig { platform: string; base_url: string; app_id: string; has_secret: boolean; }
interface ImageModel { id: string; name: string; cover?: string; }
interface ModelsResponse { items: ImageModel[]; ratios: string[]; image_cdn_base?: string; image_decrypt_key?: string; elapsed_ms: number; }
interface GenerationResult { id: string; status: 'pending' | 'processing' | 'completed' | 'success' | 'failed'; prompt: string; item_id?: string; results?: string[]; error_message?: string; created_at?: string; }
interface GenerationsResponse { list: GenerationResult[]; total: number; elapsed_ms: number; }

const TRANSPARENT_PIXEL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const isImageBytes = (bytes: Uint8Array) => {
  if (bytes.length < 12) return false;
  if ((bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71)
    || (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255)
    || (bytes[0] === 71 && bytes[1] === 73 && bytes[2] === 70)) return true;
  return String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
};

const imageMimeType = (bytes: Uint8Array) => {
  if (bytes[0] === 137 && bytes[1] === 80) return 'image/png';
  if (bytes[0] === 255 && bytes[1] === 216) return 'image/jpeg';
  if (bytes[0] === 71 && bytes[1] === 73) return 'image/gif';
  if (String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF') return 'image/webp';
  return 'image/png';
};

const resolveImageUrl = (src: string, cdnBase: string) => {
  if (/^(https?:|data:|blob:)/i.test(src) || src.startsWith('/')) return src;
  return cdnBase ? `${cdnBase.replace(/\/+$/, '')}/${src.replace(/^\/+/, '')}` : src;
};

const DecryptedResultImage: React.FC<{ src: string; cdnBase: string; decryptKey: string }> = ({ src, cdnBase, decryptKey }) => {
  const [displayUrl, setDisplayUrl] = useState(TRANSPARENT_PIXEL);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setFailed(false);
    setDisplayUrl(TRANSPARENT_PIXEL);
    const loadImage = async () => {
      try {
        const response = await fetch(resolveImageUrl(src, cdnBase), { mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const bytes = new Uint8Array(await response.arrayBuffer());
        if (!isImageBytes(bytes)) {
          const keyBytes = new TextEncoder().encode(decryptKey);
          if (!keyBytes.length) throw new Error('缺少图片解密参数');
          const limit = Math.min(100, bytes.length);
          for (let index = 0; index < limit; index += 1) bytes[index] ^= keyBytes[index % keyBytes.length];
        }
        if (!isImageBytes(bytes)) throw new Error('图片解密失败');
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: imageMimeType(bytes) }));
        if (active) setDisplayUrl(objectUrl);
      } catch {
        if (active) setFailed(true);
      }
    };
    void loadImage();
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src, cdnBase, decryptKey]);

  return failed
    ? <Alert type="error" showIcon message="图片加载失败" description="织梦图片地址无法下载或解密，请刷新结果后重试。" />
    : <Image src={displayUrl} width={180} preview={displayUrl !== TRANSPARENT_PIXEL} />;
};

const PolarisImageTestPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [models, setModels] = useState<ImageModel[]>([]);
  const [ratios, setRatios] = useState<string[]>(['1:1']);
  const [imageCdnBase, setImageCdnBase] = useState('');
  const [imageDecryptKey, setImageDecryptKey] = useState('2019ysapp7527');
  const [itemId, setItemId] = useState<string>();
  const [ratio, setRatio] = useState('1:1');
  const [prompt, setPrompt] = useState('一支散发青绿色微光的毛笔悬浮在古朴书案上，东方幻想风格，细节清晰，无文字');
  const [submitting, setSubmitting] = useState(false);
  const [generations, setGenerations] = useState<GenerationResult[]>([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const pollRef = useRef<number>();

  const loadGenerations = useCallback(async (silent = false) => {
    if (!silent) setResultLoading(true);
    try {
      const result = await get<GenerationsResponse>(API_ENDPOINTS.ai_management.polarisImageGenerations);
      setGenerations(result.list || []);
      const active = (result.list || []).some((row) => row.status === 'pending' || row.status === 'processing');
      if (!active && pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = undefined; }
    } catch (error) {
      if (!silent) message.error(error instanceof Error ? error.message : '生成记录查询失败');
    } finally { if (!silent) setResultLoading(false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setConnectionError('');
    setModels([]);
    setItemId(undefined);
    try {
      const status = await get<PlatformConfig>(API_ENDPOINTS.ai_management.polarisImageConfig);
      setConfig(status);
      if (status.has_secret) {
        const result = await get<ModelsResponse>(API_ENDPOINTS.ai_management.polarisImageModels);
        const nextModels = result.items || [];
        if (!nextModels.length) throw new Error('织梦开放平台没有返回可用的绘画模型');
        setModels(nextModels); setRatios(result.ratios || ['1:1']);
        setImageCdnBase(result.image_cdn_base || '');
        setImageDecryptKey(result.image_decrypt_key || '2019ysapp7527');
        setItemId(nextModels[0].id);
        await loadGenerations(true);
      } else {
        setConnectionError('管理 API 没有可用的织梦服务端凭据');
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : '织梦平台连接失败';
      setConnectionError(detail);
      message.error(detail);
    }
    finally { setLoading(false); }
  }, [loadGenerations]);

  useEffect(() => {
    void load();
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [load]);

  const generate = async () => {
    if (!itemId || !prompt.trim()) { message.warning('请选择模型并填写图片提示词'); return; }
    setSubmitting(true);
    try {
      await post(API_ENDPOINTS.ai_management.polarisImageGenerate, { prompt: prompt.trim(), item_id: itemId, ratio });
      message.success('文生图任务已提交，正在生成'); await loadGenerations(true);
      if (!pollRef.current) pollRef.current = window.setInterval(() => void loadGenerations(true), 5000);
    } catch (error) { message.error(error instanceof Error ? error.message : '文生图任务提交失败'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="ai-management-page">
      <h1 className="page-title">织梦文生图测试</h1>
      <Card bordered={false} className="ai-section-card">
        <Spin spinning={loading}>
          <Space wrap className="ai-summary">
            <Tag color="blue">平台：{config?.platform || '织梦开放平台'}</Tag>
            <Tag color="geekblue">AppID：{config?.app_id || 'AI-004'}</Tag>
            <Tag color={config?.has_secret ? 'blue' : 'red'}>服务端凭据：{config?.has_secret ? '已注入' : '未配置'}</Tag>
          </Space>
          <Alert
            type={connectionError ? 'error' : config?.has_secret ? 'success' : 'warning'}
            showIcon
            message={connectionError || (config?.has_secret ? '织梦文生图连接正常，已自动选择默认模型。' : '管理 API 未配置有效的织梦服务端凭据。')}
            description={connectionError ? '请核对 AI-004 产品密钥；凭据仅保存在服务端，不会发送到浏览器。' : undefined}
          />
          <Button icon={<ReloadOutlined />} onClick={load} style={{ marginTop: 16 }}>重新检查连接</Button>
        </Spin>
      </Card>

      <Card bordered={false} className="ai-section-card" title="生成图片">
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Select value={itemId} onChange={setItemId} disabled={!models.length} placeholder="连接成功后自动选择默认绘画模型" options={models.map((model) => ({ value: model.id, label: model.name }))} style={{ width: '100%' }} />
          <Select value={ratio} onChange={setRatio} options={ratios.map((value) => ({ value, label: value }))} style={{ width: '100%' }} />
          <TextArea rows={5} value={prompt} onChange={(event) => setPrompt(event.target.value)} maxLength={4000} showCount placeholder="描述希望生成的图片" />
          <Button type="primary" icon={<PictureOutlined />} loading={submitting} disabled={Boolean(connectionError) || !config?.has_secret || !models.length} onClick={generate}>生成图片</Button>
        </Space>
      </Card>

      <Card bordered={false} className="ai-section-card" title="最近生成结果" extra={<Button loading={resultLoading} onClick={() => void loadGenerations()}>刷新结果</Button>}>
        {generations.length ? generations.map((row) => (
          <div className="ai-result-block" key={row.id}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space wrap><Tag color={row.status === 'success' || row.status === 'completed' ? 'green' : row.status === 'failed' ? 'red' : 'gold'}>{row.status}</Tag><Typography.Text type="secondary">{row.created_at}</Typography.Text></Space>
              <Typography.Text>{row.prompt}</Typography.Text>
              {row.error_message && <Alert type="error" showIcon message={row.error_message} />}
              {!!row.results?.length && <Image.PreviewGroup>{row.results.map((url) => <DecryptedResultImage key={url} src={url} cdnBase={imageCdnBase} decryptKey={imageDecryptKey} />)}</Image.PreviewGroup>}
            </Space>
          </div>
        )) : <Empty description="暂无文生图记录" />}
      </Card>
    </div>
  );
};

export default PolarisImageTestPage;
